import { useState } from 'react';
import type { PoolOfferConfig } from '../../lib/api/operatorConsole';
import { PercentInput } from './PercentInput';
import { Switch } from './Switch';
import {
  FIXED_RATE_LEVELS,
  fixedRate,
  fixedRatesPatch,
  hasFixedRates,
  MAX_LOOT_RTP,
  MAX_WIN_RATE,
  MIN_FIXED_RATE,
  type FixedRateFigure,
  type FixedRateLevel,
  type FixedRatesResult,
  type PricingPatch,
} from './pricing';

type Props = {
  config: PoolOfferConfig;
  name: string;
  onSave: (poolOfferId: number, patch: PricingPatch) => Promise<FixedRatesResult>;
};

/** Shown under the level it names, or under the switch when turning fixed rates off was refused; `pricing` is what it answered. */
type Refusal = { under: FixedRateLevel['key'] | 'switch'; message: string; pricing: string };

const DRAFT_HINT = 'Fill in all six numbers, then turn the switch on.';
const READY_HINT = 'All six numbers are in. Turn the switch on to apply them.';
const ON_HINT =
  'A number saves when you press Enter or leave its field. Turn the switch off to go back to the RTPs in the table.';
const OFF_REFUSED_HINT =
  "Turning off would bring back the RTPs saved in the table, and the pack's cards can't pay them right now.";

const TURN_OFF_AND_CLEAR: PricingPatch = {
  fixed_rates: false,
  estimated_value: null,
  estimated_value_high: null,
  estimated_value_max: null,
};

const column = 'w-24 shrink-0';

/** The saved pricing a refusal answers: once any of it changes, here or elsewhere, the refusal is out of date. */
function savedPricing(c: PoolOfferConfig): string {
  return JSON.stringify([
    c.pool_offer_price,
    c.fixed_rates,
    c.max_extremes,
    ...FIXED_RATE_LEVELS.flatMap((level) => [c[level.rtp], c[level.winRate]]),
    c.estimated_value,
    c.estimated_value_high,
    c.estimated_value_max,
  ]);
}

/** While fixed rates are off the six figures are a draft the switch saves in one PATCH: the backend takes the mode only with all six. */
export function FixedRatesEditor({ config, name, onSave }: Props) {
  const on = hasFixedRates(config);
  const pricing = savedPricing(config);
  const [drafts, setDrafts] = useState<Partial<Record<FixedRateFigure, number>>>({});
  const [maxExtremesDraft, setMaxExtremesDraft] = useState<boolean | null>(null);
  const [lastRefusal, setRefusal] = useState<Refusal | null>(null);
  const refusal = lastRefusal?.pricing === pricing ? lastRefusal : null;
  const [saving, setSaving] = useState(false);
  // The switch's success wipes the drafts, so the figures wait for it.
  const [switching, setSwitching] = useState(false);

  const figure = (field: FixedRateFigure) => (on ? undefined : drafts[field]) ?? fixedRate(config, field);
  const maxExtremes = (on ? null : maxExtremesDraft) ?? config.max_extremes;
  const complete = FIXED_RATE_LEVELS.every(
    (level) => figure(level.rtp) != null && figure(level.winRate) != null,
  );

  async function save(patch: PricingPatch): Promise<boolean> {
    setSaving(true);
    try {
      const result = await onSave(config.pool_offer_id, patch);
      // Turning off is refused over the level RTPs it brings back, not over the fixed figures on screen.
      const turningOff = on && patch.fixed_rates === false;
      setRefusal(
        result.refusal && {
          under: turningOff ? 'switch' : result.refusal.level,
          message: result.refusal.message,
          pricing,
        },
      );
      return result.saved;
    } finally {
      setSaving(false);
    }
  }

  async function saveSwitch(patch: PricingPatch): Promise<boolean> {
    setSwitching(true);
    try {
      return await save(patch);
    } finally {
      setSwitching(false);
    }
  }

  function turnOffAndClear() {
    const confirmed = window.confirm(
      `Turn off fixed rates for ${name} and clear its level RTPs? The pack goes back to its own odds, and the level RTPs saved on it are removed.`,
    );
    if (confirmed) void saveSwitch(TURN_OFF_AND_CLEAR);
  }

  async function toggle() {
    if (on) {
      await saveSwitch({ fixed_rates: false });
      return;
    }
    const patch: PricingPatch = { fixed_rates: true };
    for (const level of FIXED_RATE_LEVELS) {
      Object.assign(
        patch,
        fixedRatesPatch(config, level, { rtp: figure(level.rtp)!, winRate: figure(level.winRate)! }),
      );
    }
    if (maxExtremes !== config.max_extremes) patch.max_extremes = maxExtremes;
    if (await saveSwitch(patch)) {
      setDrafts({});
      setMaxExtremesDraft(null);
    }
  }

  function clearRefusalOf(level: FixedRateLevel['key']) {
    if (refusal?.under === level) setRefusal(null);
  }

  function commitFigure(level: FixedRateLevel, kind: 'rtp' | 'winRate') {
    return async (value: number): Promise<boolean> => {
      clearRefusalOf(level.key);
      if (!on) {
        setDrafts((prev) => ({ ...prev, [level[kind]]: value }));
        return true;
      }
      const patch = fixedRatesPatch(config, level, kind === 'rtp' ? { rtp: value } : { winRate: value });
      // A figure that rounds to the saved one has nothing to save: the field shows the saved one again.
      if (Object.keys(patch).length === 0) return false;
      return save(patch);
    };
  }

  function changeMaxExtremes(checked: boolean) {
    clearRefusalOf('max');
    if (on) void save({ max_extremes: checked });
    else setMaxExtremesDraft(checked);
  }

  return (
    <div role="group" aria-label={`Fixed rates for ${name}`} className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <Switch
          checked={on}
          label={`Use fixed rates for ${name}`}
          disabled={saving || (!on && !complete)}
          onClick={() => void toggle()}
        />
        <div>
          <div className="text-[12px] font-medium text-[var(--co-text)]">Fixed rates {on ? 'on' : 'off'}</div>
          <p className="text-[11px] text-[var(--co-muted)]">
            RTP: how much of the pack price goes back to players on average. Win rate: how often a rip pulls a
            card worth the pack price or more. Each level has its own pair.
          </p>
          <p className="text-[11px] text-[var(--co-muted)]">{on ? ON_HINT : complete ? READY_HINT : DRAFT_HINT}</p>
          {refusal?.under === 'switch' && (
            <div className="mt-2 flex flex-col items-start gap-1.5">
              <div role="alert" className="text-[11px] font-medium text-[var(--co-error)]">
                <p>{refusal.message}</p>
                <p>{OFF_REFUSED_HINT}</p>
              </div>
              <button
                type="button"
                aria-label={`Turn off and clear level RTPs for ${name}`}
                disabled={saving}
                onClick={turnOffAndClear}
                className="rounded px-2 py-1 text-[11px] font-semibold text-[var(--co-error)] ring-1 ring-[var(--co-error)] hover:bg-[var(--co-surface)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Turn off and clear level RTPs
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 pl-12">
        <div className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-wide text-[var(--co-muted)]">
          <span className="w-16 shrink-0">Level</span>
          <span className={column}>RTP</span>
          <span className={column}>Win rate</span>
        </div>
        {FIXED_RATE_LEVELS.map((level) => (
          <div key={level.key} role="group" aria-label={`${level.label} level`}>
            <div className="flex items-center gap-3">
              <span className="w-16 shrink-0 text-[12px] text-[var(--co-text)]">{level.label}</span>
              <span className={column}>
                <PercentInput
                  label={`${level.label} fixed RTP for ${name}`}
                  value={figure(level.rtp)}
                  min={MIN_FIXED_RATE}
                  max={MAX_LOOT_RTP}
                  disabled={switching}
                  onCommit={commitFigure(level, 'rtp')}
                />
              </span>
              <span className={column}>
                <PercentInput
                  label={`${level.label} win rate for ${name}`}
                  value={figure(level.winRate)}
                  min={MIN_FIXED_RATE}
                  max={MAX_WIN_RATE}
                  disabled={switching}
                  onCommit={commitFigure(level, 'winRate')}
                />
              </span>
              {level.key === 'max' && (
                <label className="inline-flex items-center gap-2 text-[12px] text-[var(--co-text)]">
                  <input
                    type="checkbox"
                    checked={maxExtremes}
                    disabled={saving}
                    aria-label={`Max: extremes only for ${name}`}
                    onChange={(e) => changeMaxExtremes(e.target.checked)}
                    className="h-4 w-4 cursor-pointer accent-[var(--co-brand)]"
                  />
                  Max: extremes only
                  <span className="text-[11px] text-[var(--co-muted)]">
                    Max pulls only cheap cards, up to 0.4× the pack price, or big ones, 5× and up
                  </span>
                </label>
              )}
            </div>
            {refusal?.under === level.key && (
              <p role="alert" className="mt-1 pl-[4.75rem] text-[11px] font-medium text-[var(--co-error)]">
                {refusal.message}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
